import { useEffect, useSyncExternalStore } from "react";

/**
 * One nudge on screen at a time.
 *
 * The cards that ask the reader for something — take the new build, turn on
 * notifications, add a passkey, install the app — used to be independent,
 * and mostly that was fine because most pairs cannot happen together. But
 * the first launch of a freshly installed app raises the passkey offer and
 * the notifications offer within a second of each other, and two questions
 * arriving together get one dismissal between them: the reader clears the
 * screen, and both answers are "no" when only one was asked. The update card
 * and the notification card also occupied the same slot and needed a body
 * attribute plus a measured `--bottom-card-h` to stack — the two-bottom-cards
 * problem CLAUDE.md documents. That contract is retired: every prompt now
 * claims a turn here and renders only while it holds one.
 *
 * So they queue. The one that matters most shows; the rest wait, and their
 * own snooze rules decide whether they come back next launch. Nothing is lost
 * by waiting — every nudge here recurs. Ported from PropPay LX.
 *
 * Most important first, and the order is an argument rather than a preference:
 *
 *   session   You are signed out. Nothing else on the screen is actionable,
 *             and every other nudge is about an app you are not in right now.
 *             (Claimed by the signed-out card, components/SessionEndedCard.)
 *   card      The company admin's company has no credit card on file and a
 *             deadline after which the whole team is locked out
 *             (shared/cardRule.ts). Losing the app outranks any offer.
 *   update    A stale build is the thing most likely to make the rest of the
 *             app behave oddly, and taking it costs one tap.
 *   install   On an iPhone this GATES push — offering notifications to
 *             somebody who cannot receive them is a question with no answer.
 *   push      What the install card just promised. It follows immediately.
 *   passkey   A convenience, offered again on every fresh sign-in — so it is
 *             the one that loses least by waiting.
 */
export type NudgeId = "session" | "card" | "update" | "install" | "push" | "passkey";

export const NUDGE_ORDER: readonly NudgeId[] = ["session", "card", "update", "install", "push", "passkey"];

const rank = (id: NudgeId) => NUDGE_ORDER.indexOf(id);

let claimed: NudgeId[] = [];
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

/** Whichever claimant ranks highest, or null when nothing is waiting. */
export function activeNudge(): NudgeId | null {
  if (claimed.length === 0) return null;
  return claimed.reduce((best, id) => (rank(id) < rank(best) ? id : best));
}

/**
 * Say that this nudge would like the screen. Idempotent: a component that
 * re-renders and claims again does not queue twice.
 */
export function claimNudge(id: NudgeId): void {
  if (claimed.includes(id)) return;
  claimed = [...claimed, id];
  emit();
}

/** Give it up — answered, dismissed, or no longer applicable. */
export function releaseNudge(id: NudgeId): void {
  if (!claimed.includes(id)) return;
  claimed = claimed.filter((x) => x !== id);
  emit();
}

const subscribe = (fn: () => void) => {
  listeners.add(fn);
  return () => void listeners.delete(fn);
};

/**
 * Whether this nudge is the one on screen.
 *
 * A component claims when it decides it wants to show, then renders only
 * while this is true. It stays claimed while it waits, so the moment the
 * nudge ahead of it is answered, it appears.
 */
export function useNudgeTurn(id: NudgeId, wants: boolean): boolean {
  const active = useSyncExternalStore(subscribe, activeNudge, () => null);
  return wants && active === id;
}

/**
 * Claim a turn while `wants` is true, give it up when it stops being true or
 * the component unmounts, and say whether it is this nudge's turn now. The
 * one call every prompt makes.
 */
export function useNudge(id: NudgeId, wants: boolean): boolean {
  useEffect(() => {
    if (!wants) return;
    claimNudge(id);
    return () => releaseNudge(id);
  }, [id, wants]);
  return useNudgeTurn(id, wants);
}

/** Test seam. Never called by the app. */
export function resetNudgeQueue(): void {
  claimed = [];
  emit();
}
